/**
 * Company / Site 변경 후 React Query 캐시를 동일한 패턴으로 맞춤:
 * 1) setQueryData로 목록·단건 즉시 반영
 * 2) invalidateQueries로 스코프 전체 재검증
 */
import type { QueryClient } from '@tanstack/react-query';
import type { Company } from '@packages/types/company';
import type { SiteResponse, SiteInfoResponse } from '@packages/types/site';
import { companyManagementKeys } from '@/api/admin/useCompanyManagement';
import { authKeys } from '@/api/auth/useAuth';
import { siteKeys } from '@/api/site/siteQueryKeys';
import { useAuthStore } from '@/store/authStore';

// ---------------------------------------------------------------------------
// Company
// ---------------------------------------------------------------------------

export function applyCompanyUpdateToQueryCache(
  queryClient: QueryClient,
  updatedCompany: Company
): void {
  const id = updatedCompany.id;
  queryClient.setQueryData<Company[]>(companyManagementKeys.allList(), old =>
    old ? old.map(c => (c.id === id ? { ...c, ...updatedCompany } : c)) : old
  );
  queryClient.setQueryData<Company | undefined>(companyManagementKeys.all, old =>
    old?.id === id ? { ...old, ...updatedCompany } : old
  );
  queryClient.setQueryData(companyManagementKeys.detail(id), updatedCompany);

  const { user, updateUser } = useAuthStore.getState();
  if (user?.company_id === id) {
    updateUser({ ...user, company_name: updatedCompany.name });
  }
}

export function invalidateCompanyRelatedQueries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: companyManagementKeys.all });
  queryClient.invalidateQueries({ queryKey: authKeys.profile() });
}

// ---------------------------------------------------------------------------
// Site
// ---------------------------------------------------------------------------

export function patchSiteListsForCompany(
  queryClient: QueryClient,
  companyId: number,
  updater: (list: SiteResponse[] | undefined) => SiteResponse[] | undefined
): void {
  queryClient.setQueryData<SiteResponse[]>(companyManagementKeys.sitesByCompany(companyId), updater);
  queryClient.setQueryData<SiteResponse[]>(siteKeys.list(companyId), updater);
}

export function applySiteDetailToQueryCache(queryClient: QueryClient, site: SiteResponse): void {
  queryClient.setQueryData(siteKeys.detail(site.id), site);
  queryClient.setQueryData<SiteInfoResponse>(siteKeys.info(site.id), old =>
    old ? { ...old, site: { ...old.site, ...site } } : old
  );
}

export function removeSiteDetailFromQueryCache(queryClient: QueryClient, siteId: number): void {
  queryClient.removeQueries({ queryKey: siteKeys.detail(siteId) });
  queryClient.removeQueries({ queryKey: siteKeys.info(siteId) });
}

export function invalidateSiteRelatedQueries(queryClient: QueryClient, companyId: number): void {
  queryClient.invalidateQueries({ queryKey: siteKeys.list(companyId) });
  queryClient.invalidateQueries({ queryKey: companyManagementKeys.sitesByCompany(companyId) });
  queryClient.invalidateQueries({ queryKey: ['userSite'] });
  queryClient.invalidateQueries({ queryKey: ['mySites'] });
  queryClient.invalidateQueries({ queryKey: authKeys.profile() });
  // 사이트 담당 매니저 변경 시 오른쪽 Site Manager 테이블(Managed Sites)도 맞춤
  queryClient.invalidateQueries({ queryKey: ['siteManagers'] });
}
